import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL } from "../layouts/constants";

export default function UserList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    memberId: "",
    userName: "",
  });

  const usersPerPage = 10;

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    let userData = {};
    try {
      userData = JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      userData = {};
    }

    if (!token || !isLoggedIn) {
      localStorage.clear();
      navigate("/", { replace: true });
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/users`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (dashboardRes.status === 401 || dashboardRes.status === 403) {
        localStorage.clear();
        navigate("/", { replace: true });
        return;
      }

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!usersRes.ok) throw new Error("Failed to fetch users");

      const dashboardResult = await dashboardRes.json();
      const usersResult = await usersRes.json();

      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const rawUsers = usersResult?.data || usersResult?.users || [];

      const normalized = rawUsers.map((user, index) => {
        const rawStatus = String(user.status ?? user.fld_Approve ?? "pending").toLowerCase();

        const statusMap = {
          approved: "approved",
          pending: "pending",
          disapproved: "disapproved",
          active: "approved",
          inactive: "disapproved",
        };

        return {
          id: user.SINo ?? index + 1,
          member_id: user.member_id ?? user.fld_memberId ?? user.fld_memberld ?? "",
          name: user.name ?? user.fld_name ?? "",
          username: user.username ?? user.fld_username ?? "",
          email: user.email ?? user.fld_mail ?? "",
          password_display: "********",
          role: user.role ?? user.fld_category ?? "Guard",
          designation: user.designation ?? user.fld_designation ?? "",
          status: statusMap[rawStatus] ?? "pending",
        };
      });

      setUsers(normalized);
    } catch (error) {
      console.error("User list fetch error:", error);
      toast.error(error.message || "Failed to load user list.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !search ||
        String(user.member_id).toLowerCase().includes(search) ||
        String(user.name).toLowerCase().includes(search) ||
        String(user.username).toLowerCase().includes(search) ||
        String(user.email).toLowerCase().includes(search);

      const matchesRole = !roleFilter || user.role === roleFilter;
      const matchesStatus = !statusFilter || user.status === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: users.length,
      approved: users.filter((u) => u.status === "approved").length,
      pending: users.filter((u) => u.status === "pending").length,
      disapproved: users.filter((u) => u.status === "disapproved").length,
    };
  }, [users]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));

  const currentUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  const handleApproval = async (memberId, action, checked) => {
    let newStatus = "pending";

    if (action === "approve" && checked) newStatus = "approved";
    if (action === "disapprove" && checked) newStatus = "disapproved";

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/users/${memberId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error("Failed to update status");

      setUsers((prev) =>
        prev.map((user) =>
          user.member_id === memberId ? { ...user, status: newStatus } : user
        )
      );

      toast.success("User status updated successfully.");
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(error.message || "Failed to update status.");
    }
  };

  const confirmDelete = (memberId, userName) => {
    setDeleteModal({
      open: true,
      memberId,
      userName,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      open: false,
      memberId: "",
      userName: "",
    });
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/users/${deleteModal.memberId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to delete user");

      setUsers((prev) =>
        prev.filter((user) => user.member_id !== deleteModal.memberId)
      );

      toast.success("User deleted successfully.");
      closeDeleteModal();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.message || "Failed to delete user.");
    }
  };

  const addNewUser = () => navigate("/add_user");

  const editUser = (memberId) => {
    navigate(`/add_user?mode=edit&member_id=${encodeURIComponent(memberId)}`);
  };

  const managePermissions = (memberId) => {
    navigate(`/permission_modules?mode=edit&member_id=${encodeURIComponent(memberId)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading users...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="user-list"
      activeMenu="user-list"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />
      
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          User Management
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Manage system users, approvals and permissions
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                User List
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Search, filter, approve, edit and remove users
              </p>
            </div>
            <button
              type="button"
              onClick={addNewUser}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              + Add New User
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Users
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {stats.total}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Approved
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.approved}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Pending
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.pending}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Disapproved
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.disapproved}
            </p>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Search by name, username, email, or member ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
            >
              <option value="">All Roles</option>
              <option value="Admin">Admin</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Guard">Guard</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
            >
              <option value="">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="disapproved">Disapproved</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-5 py-[18px] border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0">
              Users Database
            </p>
            <span className="text-[12px] text-[var(--text-muted)]">
              {filteredUsers.length} entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse">
              <thead>
                <tr>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    Member ID
                  </th>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    User
                  </th>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    Username
                  </th>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    Password
                  </th>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    Role
                  </th>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    Status
                  </th>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    Approval
                  </th>
                  <th className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-[var(--text-muted)]">
                      No users found in the database.
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user, index) => (
                    <tr key={`${user.member_id}-${index}`} className="hover:bg-[var(--accent-faint)]">
                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle">
                        <span className="text-[var(--accent)] font-extrabold">
                          {user.member_id || "—"}
                        </span>
                      </td>

                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle">
                        <div className="flex items-center gap-2.5">
                          <div className="w-[38px] h-[38px] rounded-xl bg-[var(--btn-bg)] text-[var(--btn-text)] flex items-center justify-center text-[14px] font-extrabold flex-shrink-0 shadow-[0_3px_12px_var(--accent-glow)]">
                            {String(user.name || "U").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-[13px] font-extrabold text-[var(--text-primary)] m-0">
                              {user.name || "—"}
                            </p>
                            <p className="text-[12px] text-[var(--text-muted)] m-0">
                              {user.email || "—"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle">
                        {user.username || "—"}
                      </td>

                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle tracking-[2px] font-mono">
                        {user.password_display}
                      </td>

                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle">
                        <RoleBadge role={user.role} />
                      </td>

                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle">
                        <StatusBadge status={user.status} />
                      </td>

                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle">
                        <div className="flex flex-col gap-1.5">
                          <label className="flex items-center gap-1.5 text-[12px] font-bold cursor-pointer text-[var(--text-muted)]">
                            <input
                              type="checkbox"
                              checked={user.status === "approved"}
                              onChange={(e) =>
                                handleApproval(user.member_id, "approve", e.target.checked)
                              }
                              className="accent-[var(--accent)]"
                            />
                            Approve
                          </label>
                          <label className="flex items-center gap-1.5 text-[12px] font-bold cursor-pointer text-[var(--text-muted)]">
                            <input
                              type="checkbox"
                              checked={user.status === "disapproved"}
                              onChange={(e) =>
                                handleApproval(user.member_id, "disapprove", e.target.checked)
                              }
                              className="accent-[var(--accent)]"
                            />
                            Disapprove
                          </label>
                        </div>
                      </td>

                      <td className="px-[18px] py-[15px] border-b border-[var(--divider)] text-[13px] align-middle">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => editUser(user.member_id)}
                            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-2.5 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] hover:text-[var(--accent)]"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => managePermissions(user.member_id)}
                            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-2.5 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] hover:text-[var(--accent)]"
                          >
                            Permissions
                          </button>
                          <button
                            type="button"
                            onClick={() => confirmDelete(user.member_id, user.name)}
                            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-2.5 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--red-faint)] hover:border-[var(--red-border)] hover:text-[var(--red)]"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-[15px] border-t border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-[var(--text-muted)]">
              Showing {currentUsers.length === 0 ? 0 : (currentPage - 1) * usersPerPage + 1} to{" "}
              {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length}{" "}
              entries
            </div>

            <div className="flex gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  type="button"
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`border rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer ${
                    currentPage === i + 1
                      ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
                      : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)]"
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                type="button"
                onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        {deleteModal.open && (
          <div className="fixed inset-0 z-[9999] bg-black/55 flex items-center justify-center p-[18px]" onClick={closeDeleteModal}>
            <div className="w-full max-w-[460px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[18px] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.45)]" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-[20px] font-extrabold text-[var(--text-primary)] m-0 mb-2.5">
                Confirm Delete
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mb-3 leading-relaxed">
                Are you sure you want to delete user{" "}
                <strong>{deleteModal.userName || "this user"}</strong>?
              </p>
              <p className="text-[12px] text-[var(--red)] m-0 mb-[18px] font-bold">
                This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2.5">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="rounded-xl px-3.5 py-2.5 text-[13px] font-extrabold cursor-pointer bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-primary)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="rounded-xl px-3.5 py-2.5 text-[13px] font-extrabold cursor-pointer bg-[var(--red)] border border-[var(--red)] text-white"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function RoleBadge({ role }) {
  const lower = String(role || "").toLowerCase();

  let bg = "bg-[rgba(34,197,94,0.12)]";
  let border = "border-[rgba(34,197,94,0.28)]";
  let text = "text-[#22c55e]";

  if (lower === "admin") {
    bg = "bg-[var(--accent-faint)]";
    border = "border-[var(--accent-border)]";
    text = "text-[var(--accent)]";
  } else if (lower === "supervisor") {
    bg = "bg-[rgba(168,85,247,0.12)]";
    border = "border-[rgba(168,85,247,0.28)]";
    text = "text-[#a855f7]";
  }

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-extrabold ${bg} ${border} ${text}`}>
      {role || "—"}
    </span>
  );
}

function StatusBadge({ status }) {
  const value = String(status || "pending").toLowerCase();

  let bg = "bg-[rgba(245,158,11,0.12)]";
  let border = "border-[rgba(245,158,11,0.28)]";
  let text = "text-[#f59e0b]";

  if (value === "approved") {
    bg = "bg-[rgba(34,197,94,0.12)]";
    border = "border-[rgba(34,197,94,0.28)]";
    text = "text-[#22c55e]";
  } else if (value === "disapproved") {
    bg = "bg-[var(--red-faint)]";
    border = "border-[var(--red-border)]";
    text = "text-[var(--red)]";
  }

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-extrabold ${bg} ${border} ${text}`}>
      ● {value.charAt(0).toUpperCase() + value.slice(1)}
    </span>
  );
}